import { useQuery, UseQueryResult } from 'react-query';
import { MediaApi } from 'api/media';
import { queryKey } from 'constants/queryKey';
import { MediaLibraryAlbum } from 'types/MediaLibrary';
import {
  AlbumScope,
  albumScopeListParams,
  isBrowsableScope,
} from 'app/modules/mediaLibraryPage/utils/albumScope';

export interface AlbumTreeNode {
  album: MediaLibraryAlbum;
  depth: number;
}

function sortAlbums(albums: MediaLibraryAlbum[]): MediaLibraryAlbum[] {
  return [...albums].sort(
    (a, b) =>
      Number(b.is_default) - Number(a.is_default) ||
      a.name.localeCompare(b.name),
  );
}

/**
 * Depth-annotated and parent-before-child, sorted at every level. The response
 * is flat and in no useful order. An album whose parent isn't in it, because
 * that branch isn't reachable, counts as a root.
 */
function buildAlbumTreeNodes(albums: MediaLibraryAlbum[]): AlbumTreeNode[] {
  const albumIds = new Set(albums.map((album) => album.id));
  const childrenByParent = new Map<string | null, MediaLibraryAlbum[]>();
  albums.forEach((album) => {
    const parentId =
      album.parent_id && albumIds.has(album.parent_id) ? album.parent_id : null;
    childrenByParent.set(parentId, [
      ...(childrenByParent.get(parentId) ?? []),
      album,
    ]);
  });

  const nodesBelow = (
    parentId: string | null,
    depth: number,
  ): AlbumTreeNode[] =>
    sortAlbums(childrenByParent.get(parentId) ?? []).flatMap((album) => [
      { album, depth },
      ...nodesBelow(album.id, depth + 1),
    ]);

  return nodesBelow(null, 0);
}

/** The full album tree of a profile or of the shared albums, as the Move picker lists it. */
export default function useAlbumTreeForMove(
  scope: AlbumScope | undefined,
  enabled: boolean,
): UseQueryResult<AlbumTreeNode[]> {
  return useQuery(
    queryKey.mediaLibraryPage.albumTree(scope ?? 0),
    async () =>
      isBrowsableScope(scope)
        ? buildAlbumTreeNodes(
            await MediaApi.getAlbumTree(albumScopeListParams(scope)),
          )
        : [],
    { enabled: enabled && isBrowsableScope(scope) },
  );
}
